import {Object3D} from '@wonderlandengine/api';
import {vec3} from 'gl-matrix';

const tempVec3 = vec3.create();

interface Animation {
    start: number;
    duration: number;
    update: (time: number) => void;
    onComplete?: () => void;
}

export function move(o: Object3D, from: vec3, to: vec3) {
    return (f: number) => o.setPositionLocal(vec3.lerp(tempVec3, from, to, f));
}

export function rotateLocal(o: Object3D, axis: vec3, from: number, to: number) {
    return (f: number) => {
        o.resetRotation();
        o.rotateAxisAngleDegLocal(axis, from * f + to * (1 - f));
    };
}

export function scale(o: Object3D, from: vec3, to: vec3) {
    return (f: number) => o.setScalingLocal(vec3.lerp(tempVec3, from, to, f));
}

export function uniformScale(o: Object3D, from: number, to: number) {
    return (f: number) => {
        const s = f * to + (1 - f) * from;
        vec3.set(tempVec3, s, s, s);
        o.setScalingLocal(tempVec3);
    };
}

export async function delay(durationSeconds: number) {
    return new Promise((res, rej) => {
        setTimeout(res, durationSeconds * 1000);
    });
}

let time = 0;
const effects: Animation[] = [];

export function updateAnimations(dt: number) {
    time += dt;

    for (let i = effects.length - 1; i >= 0; --i) {
        const e = effects[i];
        const f = Math.min((time - e.start) / e.duration, 1);
        e.update(f);
        if (f >= 1.0) {
            if (e.onComplete) {
                e.onComplete();
                effects.splice(i, 1);
            } else {
                e.start = time;
            }
        }
    }
}
export function stop(e: Animation) {
    const i = effects.indexOf(e);
    effects.splice(i, 1);
}

export function play(update: (time: number) => void, duration: number): Promise<void> {
    return new Promise((res, _) => {
        const e = {
            start: time,
            duration,
            update,
            onComplete: res,
        };
        effects.push(e);
    });
}

/**
 * Loop an animation infinitely, stop it with @ref stop(e), where e is the return value
 */
export function loop(update: (time: number) => void, duration: number): Animation {
    const e = {
        start: time,
        duration,
        update,
    };
    effects.push(e);
    return e;
}
